/**
 * Turning a stack of outlines into a surface.
 *
 * Each pair of outlines from neighbouring layers is joined into a band of triangles, and
 * the bands together are the object's skin. Where consecutive outlines are the same shape
 * the band is a vertical wall; where the shape shrinks it is a slope. That one mechanism
 * covers both, which is why the surface is built this way rather than as a stack of
 * upright walls with the ledges between them filled in separately.
 *
 * ## Which point joins which
 *
 * Two outlines of the same feature have no reason to have the same number of points, or to
 * start in the same place. So each is walked by how far round it a point sits — nought at
 * the start, one at the end — and at each step the side that is further behind advances.
 * That gives a fan of triangles that never crosses itself, whatever the two point counts
 * are.
 *
 * Pure, like the extraction it follows: it takes points and returns triangles.
 */

import {
  areaOf,
  isAnticlockwise,
  type Chain,
  type Contour,
  type Point,
} from './contours.js';

/** Three points, nine numbers, wound so the surface faces out of the object. */
export type Triangles = Float32Array;

/**
 * Build the walls of a chain of outlines.
 *
 * @returns positions for triangles, three vertices at a time.
 */
export function loftChain(chain: Chain): number[] {
  const out: number[] = [];

  for (let index = 0; index + 1 < chain.contours.length; index += 1) {
    const lower = chain.contours[index];
    const upper = chain.contours[index + 1];
    if (!lower || !upper) continue;

    band(out, lower, upper, chain.kind);
  }

  return out;
}

/**
 * Join two outlines into a ring of triangles.
 *
 * Winding decides which way the surface faces, and the two kinds want opposite answers: an
 * outline is seen from outside the object, a hole from inside it. Both rings are first put
 * into a known direction so that the choice is made once here rather than depending on
 * which way round the printer happened to travel.
 */
function band(
  out: number[],
  lower: Contour,
  upper: Contour,
  kind: 'outline' | 'hole',
): void {
  const outward = kind === 'outline';
  const a = facing(lower.points, outward);
  const b = alignTo(facing(upper.points, outward), a);

  if (a.length < 3 || b.length < 3) return;

  const walkA = wayRound(a);
  const walkB = wayRound(b);

  let i = 0;
  let j = 0;

  while (i < a.length || j < b.length) {
    const nextA = walkA[i + 1] ?? 1;
    const nextB = walkB[j + 1] ?? 1;

    // Whichever side is further behind takes the next step, so neither runs ahead of the
    // other and the triangles stay evenly spread round the ring.
    const stepA = i < a.length && (j >= b.length || nextA <= nextB);

    const a0 = a[i % a.length];
    const b0 = b[j % b.length];
    if (!a0 || !b0) break;

    if (stepA) {
      const a1 = a[(i + 1) % a.length];
      if (!a1) break;
      triangle(out, a0, lower.height, a1, lower.height, b0, upper.height);
      i += 1;
    } else {
      const b1 = b[(j + 1) % b.length];
      if (!b1) break;
      triangle(out, a0, lower.height, b1, upper.height, b0, upper.height);
      j += 1;
    }
  }
}

function triangle(
  out: number[],
  p: Point,
  py: number,
  q: Point,
  qy: number,
  r: Point,
  ry: number,
): void {
  out.push(p[0], py, p[1], q[0], qy, q[1], r[0], ry, r[1]);
}

/**
 * Put a ring into the direction that makes its surface face the right way.
 *
 * Clockwise in the bed plane for a surface seen from outside: the scene's z axis points
 * back towards the viewer, so looking down at the bed reverses the sense that the same
 * winding would have on paper. Getting this backwards makes an object invisible from
 * outside and visible from within, which reads as a hole rather than as a mistake.
 */
function facing(points: readonly Point[], outward: boolean): Point[] {
  const ring = [...points];
  return isAnticlockwise(ring) === !outward ? ring : ring.reverse();
}

/**
 * Rotate the second ring so it begins beside the first.
 *
 * Without this the two are walked from wherever the printer happened to start each of
 * them, and the band spirals round the object instead of joining like to like.
 */
function alignTo(ring: readonly Point[], reference: readonly Point[]): Point[] {
  const start = reference[0];
  if (!start || ring.length === 0) return [...ring];

  let nearest = 0;
  let best = Infinity;

  for (let index = 0; index < ring.length; index += 1) {
    const point = ring[index];
    if (!point) continue;

    const away = Math.hypot(point[0] - start[0], point[1] - start[1]);
    if (away < best) {
      best = away;
      nearest = index;
    }
  }

  return [...ring.slice(nearest), ...ring.slice(0, nearest)];
}

/** How far round the ring each point sits, from 0 at the first to 1 back at it. */
function wayRound(ring: readonly Point[]): number[] {
  const lengths: number[] = [0];
  let total = 0;

  for (let index = 0; index < ring.length; index += 1) {
    const here = ring[index];
    const next = ring[(index + 1) % ring.length];
    if (!here || !next) continue;

    total += Math.hypot(next[0] - here[0], next[1] - here[1]);
    lengths.push(total);
  }

  if (total === 0) return lengths;
  return lengths.map((length) => length / total);
}

/**
 * Whether a chain's outlines are worth building at all.
 *
 * A chain of nothing but specks is noise — a stray closed loop somewhere in the infill,
 * or a scrap of the purge. Measured by the largest outline in it, since a cone tapers to
 * a point at the top quite legitimately.
 */
export function isSubstantial(chain: Chain, leastAreaMm2 = 1): boolean {
  return chain.contours.some((contour) => areaOf(contour.points) >= leastAreaMm2);
}
